import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';

import { ELaunchDarklyFlag, Microservices } from 'src/app/core/constants/pol.enums';
import { IAddUserSearchResponse, IUser } from 'src/app/core/models/state.model';
import { getSignedInUser } from 'src/app/core/state/core.selectors';
import {
  IAddEditUserForm,
  IAddUserSearchCriteria,
  IChildUserRoleDetails,
  IEditUserActiveResponse,
  IEditUserRemoveRequestData,
  IExistingUsersSearchCriteria,
  IGetEachVcodeDetailRes,
  IPendingUser,
  IPendingUserResponse,
  IRoleAssignmentConfigResponse,
  ISecurityGroupByOaaResponse,
  ISecurityGroupSet,
  IUserPreferencesResponse,
  IVcodeIndividualData,
  SaveContactPayload
} from './state/security-management.model';
import { Toast } from 'src/app/core/decorators/toast.decorator';
import { Helpers } from 'src/app/utils/helpers';
import { ProviderService } from 'src/app/core/services/provider.service';
import { LaunchDarklyService } from 'src/app/core/layout/launch-darkly/launch-darkly.service';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class SecurityManagementService implements OnDestroy {
  launchDarkly = inject(LaunchDarklyService);
  previousUrl: string;
  signedInUser: IUser;
  choseRoleToDataViewPage: string;
  signUpFlag: boolean = false;
  signInFlag = false;

  private assignRolesVcodes = {
    providers: {},
    offices: {},
    claims: {},
    financials: {}
  };
  private saveEachVcodeDetails: { [key: string]: IGetEachVcodeDetailRes } = {};

  private storeGetProviderVcodes$: BehaviorSubject<any> = new BehaviorSubject({});

  private storeGetEachVcodeDetails$: BehaviorSubject<any> = new BehaviorSubject({});

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  user$: Observable<IUser> = this.store.select(getSignedInUser).pipe(takeUntil(this.ngUnsubscribe));

  constructor(
    private http: HttpClient,
    private store: Store,
    private providerService: ProviderService,
  ) {
    this.user$.subscribe((user) => (this.signedInUser = user));

    // Get Launch Darkly Flag value
    this.launchDarkly.flags$.subscribe(res => {
      this.signUpFlag = res[ELaunchDarklyFlag.SignUp];
      this.signInFlag = res[ELaunchDarklyFlag.SignIn];
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  @Toast({
    error: {
      title: { 400: 'Security Management' },
      message: { 400: 'Unable to find security groups.  Please try again.' },
      disableTimeOut: true,
      closeButton: true
    }
  })
  getSecuritySets(): Observable<ISecurityGroupSet[]> {
    const apiUrl = `${Microservices.SecurityGroup}/SecurityGroup/SecurityGroupByOaa`;
    let queryParams = new HttpParams();
    queryParams = queryParams.append('request.providerType', this.signedInUser.providerType);
    return this.http
      .get<ISecurityGroupByOaaResponse>(apiUrl, { params: queryParams })
      .pipe(map((res) => res.securityGroupsByOaa));
  }

  fetchUserSearchAutoCompleteResult(
    value: string,
    restrictOaaSecurityGroups = false
  ): Observable<{ results: string[] }> {
    const apiUrl = `${Microservices.SecurityGroup
      }/SecurityGroup/Search/AutoComplete?request.searchText=${encodeURIComponent(
        value
      )}&request.restrictToOaaSecurityGroups=${restrictOaaSecurityGroups}`;
    let queryParams = new HttpParams();
    queryParams = queryParams.append('request.providerType', this.signedInUser?.providerType);
    return this.http.get<{ results: string[] }>(apiUrl, { params: queryParams });
  }

  fetchOaaUserSearchAutoCompleteResult(value: string, securityGroupId: string): Observable<{ results: string[] }> {
    const apiUrl = `${Microservices.SecurityGroup
      }/SecurityGroup/Search/AutoComplete?request.searchText=${encodeURIComponent(
        value
      )}&request.securityGroupId=${securityGroupId}`;
    let queryParams = new HttpParams();
    queryParams = queryParams.append('request.providerType', this.signedInUser?.providerType);
    return this.http.get<{ results: string[] }>(apiUrl, { params: queryParams });
  }

  checkExistingUserId(userId: string): Observable<any> {
    const apiUrl = `${Microservices.SecurityGroup}/public/UserIdIsAvailable?userId=${userId}`;
    return this.http.get<{ isAvailable: boolean }>(apiUrl);
  }

  // assign user role:
  applyAddUser(submitObj): Observable<any> {
    const apiUrl = `${Microservices.SecurityGroup}/SecurityGroup/AddUser`;
    return this.http.post<{}>(apiUrl, submitObj);
  }

  getChildUserDetails(securityGroupId: string, childUserName: string, invitationId?: string): Observable<any> {
    let apiUrl = `${Microservices.SecurityGroup}/SecurityGroup/${securityGroupId}/ChildUser?childUserName=${encodeURIComponent(childUserName)}`;

    if (this.signUpFlag) {
      let params = []

      if (childUserName) {
        params.push(`childUserName=${encodeURIComponent(childUserName)}`);
      }

      if (invitationId) {
        params.push(`invitationId=${invitationId}`);
      }

      apiUrl = `${Microservices.SecurityGroup}/v2/SecurityGroup/${securityGroupId}/ChildUser?${params.join('&')}`;
    }

    return this.http.get<any>(apiUrl).pipe(
      map((res: IChildUserRoleDetails) => {
        res.roleAssignmentConfig.sort((a: IRoleAssignmentConfigResponse, b: IRoleAssignmentConfigResponse) =>
          Helpers.sort(a.display, b.display)
        );
        return res;
      })
    );
  }

  checkExistingNavinetId(navinetId: string): Observable<any> {
    const apiUrl = `${Microservices.SecurityGroup}/Users/NavinetIdIsAvailable?navinetId=${navinetId}`;
    return this.http.get<{ navinetIdIsAvailable: boolean }>(apiUrl);
  }

  // get&cache multiRole-vcode:
  getVcodeGeneralData(securityGroupId: string, roleType: string, childUserName?: string, invitationId?: string): Observable<any> {
    let apiUrl: string;

    if (this.signUpFlag) {
      // if flag is enable use V2
      let params = []

      if (childUserName) {
        params.push(`childUserName=${encodeURIComponent(childUserName)}`);
      }

      if (invitationId) {
        params.push(`invitationId=${invitationId}`);
      }

      apiUrl = `${Microservices.SecurityGroup}/v2/SecurityGroup/${securityGroupId}/ChildUser/Assign${roleType}?${params.join('&')}`;
    } else {
      // if flag is disabled use V1
      apiUrl = `${Microservices.SecurityGroup}/SecurityGroup/${securityGroupId}/ChildUser/Assign${roleType}?childUserName=${encodeURIComponent(childUserName)}`
    }

    if (this.assignRolesVcodes[roleType.toLowerCase()]) {
      if (Object.keys(this.assignRolesVcodes[roleType.toLowerCase()]).length) {
        this.storeGetProviderVcodes$.next(this.assignRolesVcodes[roleType.toLowerCase()]);
        return this.storeGetProviderVcodes$.asObservable();
      } else {
        return this.http.get<{}>(apiUrl).pipe(
          map((res: any) => {
            let providerRes = { providers: [] };
            let officeRes = { offices: [] };
            let taxIdRes: {
              chiropractic: IVcodeIndividualData[];
              financials: IVcodeIndividualData[];
              helios: IVcodeIndividualData[];
            };
            switch (roleType.toLowerCase()) {
              case 'providers':
                providerRes = {
                  ...providerRes,
                  providers: res.results
                };
                this.assignRolesVcodes.providers = providerRes;
                return providerRes;
              case 'offices':
                officeRes = {
                  ...officeRes,
                  offices: res.results
                };
                this.assignRolesVcodes.offices = officeRes;
                return officeRes;
              case 'claims':
                this.assignRolesVcodes.claims = res;
                return res;
              case 'financials':
                taxIdRes = this.mapFinancialsRes(res);
                this.assignRolesVcodes.financials = taxIdRes;
                return taxIdRes;
              default:
                return;
            }
          })
        );
      }
    }
  }

  mapFinancialsRes(res: {
    chiropractic: IVcodeIndividualData[];
    financials: IVcodeIndividualData[];
    helios: { selectedSpecialty: string; taxIds: any };
  }) {
    const mapRes: {
      chiropractic: IVcodeIndividualData[];
      financials: IVcodeIndividualData[];
      helios: IVcodeIndividualData[];
    } = { chiropractic: [], financials: [], helios: [] };
    mapRes.chiropractic = res.chiropractic;
    mapRes.financials = res.financials;
    res.helios.taxIds?.forEach((eachH: any) => {
      const mapSpecialtyList: {
        specialtyName: string;
        isSelected: boolean;
      }[] = [];
      eachH.specialties?.forEach((eachSp: string) => {
        const mapEachSpecialty: {
          specialtyName: string;
          isSelected: boolean;
        } = { specialtyName: '', isSelected: null };
        mapEachSpecialty.specialtyName = eachSp;
        mapEachSpecialty.isSelected =
          eachSp === res.helios?.selectedSpecialty && eachH.userAssignment.hasAccess ? true : false;
        mapSpecialtyList.push(mapEachSpecialty);
      });
      eachH.specialties = mapSpecialtyList;
      mapRes.helios?.push(eachH);
    });
    return mapRes;
  }

  // expand each vCode accordion
  getVcodeDetails(vcode: string): Observable<any> {
    const apiUrl = `${Microservices.SecurityGroup}/Providers/ByVcode`;
    if (this.saveEachVcodeDetails && this.saveEachVcodeDetails.hasOwnProperty(vcode)) {
      this.storeGetEachVcodeDetails$.next(this.saveEachVcodeDetails[vcode]);
      return this.storeGetEachVcodeDetails$.asObservable();
    } else {
      return this.http.post<any>(apiUrl, { vcode }).pipe(
        map((res: IGetEachVcodeDetailRes) => {
          this.saveEachVcodeDetails[vcode] = res;
          return res;
        })
      );
    }
  }

  clearStoredVcodes(): void {
    this.assignRolesVcodes = {
      providers: {},
      offices: {},
      claims: {},
      financials: {}
    };
  }

  clearStoredEachVcodeDetails() {
    this.saveEachVcodeDetails = {};
  }

  @Toast({
    error: {
      title: { 400: 'Update user permissions unsuccessful', 500: 'Save User' },
      message: { 400: 'auto', 500: 'Failed to save the user. Verify all required provider, office, and TIN selections. Please try again.' },
      disableTimeOut: true,
      closeButton: true
    }
  })
  saveUser(submitObj: any): Observable<any> {
    const uri = this.signUpFlag ? '/v2/SecurityGroup/SaveUser' : '/SecurityGroup/SaveUser';
    const apiUrl = `${Microservices.SecurityGroup}${uri}`;
    return this.http.post<any>(apiUrl, submitObj).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getProviders(body: any): Observable<any> {
    return this.providerService.getCareTypeNetworkIds().pipe(
      mergeMap((res) => {
        const payload = {
          ...body,
          networkIds: res
        };

        const apiUrl = `${Microservices.Provider}/Providers`;
        return this.http.post<any>(apiUrl, payload);
      })
    );
  }

  // add-user:
  getUserSearchResult(searchBody: IAddUserSearchCriteria, securitySetId = null): Observable<any> {
    let apiUrl = `${Microservices.SecurityGroup}/SecurityGroup/AddUserSearch?`;
    const formattedBody = this.formatUserSearchBody(searchBody, 'addUser');
    if (formattedBody?.length) {
      formattedBody.forEach((eachParam, index) => {
        apiUrl = index === 0 ? `${apiUrl}${eachParam}` : `${apiUrl}&${eachParam}`;
      });
    }
    return this.http.get<IAddUserSearchResponse<IAddUserSearchCriteria[]>>(apiUrl).pipe(
      map((res: IAddUserSearchResponse<IAddUserSearchCriteria[]>) => {
        return res?.results;
      })
    );
  }

  formatUserSearchBody(bodyContent: any, flowType: string): any[] {
    const userSearchContent = [];
    if (bodyContent.securityGroupId) {
      flowType === 'addUser'
        ? userSearchContent.push(`securityGroupId=${bodyContent.securityGroupId}`)
        : userSearchContent.push(`userNameSearchParam=${encodeURIComponent(bodyContent.userId)}`);
    }
    if (bodyContent.firstName) {
      userSearchContent.push(`firstName=${encodeURIComponent(bodyContent.firstName)}`);
    }
    if (bodyContent.lastName) {
      userSearchContent.push(`lastName=${encodeURIComponent(bodyContent.lastName)}`);
    }
    if (bodyContent.email) {
      const mappedEmail = bodyContent.email[0] === '@' ? bodyContent.email.slice(1) : bodyContent.email;
      const email = encodeURIComponent(mappedEmail);
      userSearchContent.push(`email=${email}`);
    }
    if (bodyContent.userId) {
      userSearchContent.push(`userNameSearchParam=${encodeURIComponent(bodyContent.userId)}`);
    }
    return userSearchContent;
  }

  submitAddNewUser(submitObj): Observable<any> {
    let apiUrl = `${Microservices.SecurityGroup}/User`;
    let payload: IAddEditUserForm = {
      firstName: submitObj.firstName,
      lastName: submitObj.lastName,
      middleInitial: submitObj.mi,
      isPhysician: submitObj.physician === 'Y' ? true : false,
      npiNumber: submitObj.physician === 'Y' ? submitObj.npi : '',
      email: submitObj.email,
      workPhone: submitObj.phone,
      workPhoneExtension: submitObj.phoneExt,
      phoneNumberExtension: submitObj.phoneExt,
      address1: submitObj.address1,
      address2: submitObj.address2,
      city: submitObj.city,
      state: submitObj.state,
      zip: submitObj.zipcode,
      userId: submitObj.userId,
      securityGroupId: submitObj.securityGroupId
    };

    if (this.signUpFlag) {
      apiUrl = `${Microservices.SecurityGroup}/Invitation/ChildUser`;
      delete payload.userId;
    }

    return this.http.post<{ invitationId: string }>(apiUrl, payload);
  }

  // edit-user:
  getEditUserSearchResult(searchBody: IExistingUsersSearchCriteria): Observable<any> {
    let apiUrl = `${Microservices.SecurityGroup}/SecurityGroup/EditUserSearch?`;
    let queryParams = new HttpParams();
    queryParams = queryParams.append('providerType', this.signedInUser?.providerType);
    const formattedBody = this.formatUserSearchBody(searchBody, 'editUser');
    if (formattedBody?.length) {
      formattedBody.forEach((eachParam, index) => {
        apiUrl = index === 0 ? `${apiUrl}${eachParam}` : `${apiUrl}&${eachParam}`;
      });
    }
    return this.http.get<IAddUserSearchResponse<IExistingUsersSearchCriteria[]>>(apiUrl, { params: queryParams }).pipe(
      map((res: IAddUserSearchResponse<IExistingUsersSearchCriteria[]>) => {
        return res?.results;
      })
    );
  }

  // edit user demographic
  @Toast({
    error: {
      title: 'User Details',
      useResponseAsMessage: true,
      disableTimeOut: true,
      closeButton: true
    }
  })
  getEditUserDemographic(userName: string): Observable<any> {
    const apiUrl = `${Microservices.SecurityGroup}/Users/Demographics?childUserName=${encodeURIComponent(userName)}`;
    return this.http.get<{ userPreferences: IUserPreferencesResponse }>(apiUrl).pipe(
      map((res: { userPreferences: IUserPreferencesResponse }) => {
        return res?.userPreferences;
      })
    );
  }

  @Toast({
    error: {
      title: 'Remove User',
      useResponseAsMessage: true,
      disableTimeOut: true,
      closeButton: true
    }
  })
  removeEditUser(requestBody: IEditUserRemoveRequestData): Observable<any> {
    let apiUrl = `${Microservices.SecurityGroup}/SecurityGroup/RemoveUser`;
    const payload: IEditUserRemoveRequestData = {
      securityGroupIds: requestBody.securityGroupIds,
      preferredUserName: requestBody.preferredUserName
    };
    return this.http.post<{}>(apiUrl, payload);
  }

  activeSecuritySet(preferredUserName: string): Observable<any> {
    let apiUrl = `${Microservices.SecurityGroup}/Users/SecurityGroups?preferredUserName=${preferredUserName}`;
    return this.http.get<any>(apiUrl).pipe(
      map((response: IEditUserActiveResponse) => {
        return response?.securityGroupIds;
      })
    );
  }
  submitEditUserDemographic(submitObj): Observable<any> {
    //const apiUrl = `${Microservices.SecurityGroup}/Users/Demographics`;
    let apiUrl;

    //Use V2 when flag is enabled
    if (this.signInFlag) {
      apiUrl = `${Microservices.SecurityGroup}/v2/Users/Demographics`;
      console.log('HERE', apiUrl);
    }

    if (!this.signInFlag) {
      apiUrl = `${Microservices.SecurityGroup}/Users/Demographics`;
    }

    const payload: IAddEditUserForm = {
      firstName: submitObj.firstName,
      lastName: submitObj.lastName,
      middleInitial: submitObj.mi,
      isPhysician: submitObj.physician === 'Y' ? true : false,
      npiNumber: submitObj.physician === 'Y' ? submitObj.npi : '',
      email: submitObj.email,
      workPhone: submitObj.phone,
      phoneNumber: submitObj.phone,
      workPhoneExtension: submitObj.phoneExt,
      phoneNumberExtension: submitObj.phoneExt,
      address1: submitObj.address1,
      address2: submitObj.address2,
      city: submitObj.city,
      state: submitObj.state,
      zip: submitObj.zipcode,
      userName: submitObj.userId
    };
    return this.http.put<IUserPreferencesResponse>(apiUrl, payload);
  }

  getPendingUsers(): Observable<IPendingUser[]> {
    const apiUrl = `${Microservices.SecurityGroup}/Invitations/Pending/SecurityGroup`;
    return this.http.get<IPendingUserResponse>(apiUrl).pipe(
      map((res) => {
        res.results.forEach((element) => {
          element.fullName = `${element.firstName},${element.lastName}`;
          element.created = moment(element.created).format('YYYY-MM-DD h:mm:ss A')
          element.expiresAt = moment(element.expiresAt).format('YYYY-MM-DD h:mm:ss A')
        });
        return res.results;
      })
    );
  }

  resendPendingUserInvitation(invitationId: string): Observable<any> {
    const apiUrl = `${Microservices.SecurityGroup}/Invitation/Resend`;
    return this.http.post<{}>(apiUrl, { invitationId });
  }

  cancelPendingUserInvitation(invitationId: string): Observable<any> {
    const apiUrl = `${Microservices.SecurityGroup}/Invitation/Cancel`;
    return this.http.post<{}>(apiUrl, { invitationId });
  }

  @Toast({
    success: {
      title: 'Communication Contacts',
      message: 'Addition of contacts successful',
      timeOut: 10000,
      closeButton: true,
      disableTimeOut: false,
      extendedTimeOut: 10000,
      progressBar: true,
      progressAnimation: 'increasing'
    },
    error: {
      title: 'Communication Contacts',
      message: 'Addition of contacts was not successful, please try again',
      disableTimeOut: true,
      closeButton: true
    }
  })
  saveContact(payload: SaveContactPayload): Observable<{ id: number }> {
    const apiUrl = `${Microservices.ProviderOnline}/CommunicationPreferences/SaveContact`;
    return this.http.post<{ id: number }>(apiUrl, payload);
  }

  deleteContact(id: string): Observable<void> {
    const apiUrl = `${Microservices.ProviderOnline}/CommunicationPreferences/${id}`;
    return this.http.delete<void>(apiUrl);
  }
}
